import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { UPDATE_CUSTOMER_AMC, GET_AMC_HISTORY, TOGGLE_AMC_STATUS, EXTEND_AMC_ENDPOINT } from '../utils/api';

function AmcDetailsCard({ id, token, amcData }) {
  const [amcStart, setAmcStart] = useState('');
  const [amcEnd, setAmcEnd] = useState('');
  const [amcRemark, setAmcRemark] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (amcData) {
      setAmcStart(amcData.amc_start_date || '');
      setAmcEnd(amcData.amc_end_date || '');
      setAmcRemark(amcData.amc_remark || '');
    }
    fetchAMCList();
  }, [amcData]);

  const handleAmcExtend = async (amc_id) => {
    try {
      const res = await axios.post(EXTEND_AMC_ENDPOINT, { id: amc_id });
      if (res.data.success) {
        toast.success(res.data.message);
        fetchAMCList();
      } else {
        toast.error(res.data.message || 'Failed to extend AMC');
      }
    } catch (err) {
      toast.error('Server error during AMC extension');
    }
  };

  const handleStatusToggle = async (amc_id, currentStatus) => {
    try {
      const res = await axios.post(TOGGLE_AMC_STATUS, {
        id: amc_id,
        status: currentStatus === 1 ? 0 : 1,
      });

      if (res.data.success) {
        toast.success(res.data.message);
        fetchAMCList();
      } else {
        toast.error(res.data.message || 'Status update failed');
      }
    } catch (err) {
      toast.error('Server error while updating status');
    }
  };

  const fetchAMCList = async () => {
    try {
      const res = await axios.post(GET_AMC_HISTORY, { id, customer_token: token });
      if (res.data.success) {
        setHistory(res.data.history);
      } else {
        toast.error(res.data.message || 'Failed to load AMC history');
      }
    } catch (err) {
      toast.error('Error loading AMC history');
    }
  };

  const handleSaveAMC = async () => {
    if (!amcStart || !amcEnd) {
      toast.error('Start Date and End Date are required');
      return;
    }

    try {
      setSaving(true);
      const rawToken = localStorage.getItem('token');
      const parsed = rawToken ? JSON.parse(atob(rawToken)) : null;
      const user_id = parsed?.id || 0;

      const res = await axios.post(UPDATE_CUSTOMER_AMC, {
        id,
        customer_token: token,
        amc_start_date: amcStart,
        amc_end_date: amcEnd,
        amc_remark: amcRemark,
        user_id,
      });

      if (res.data.success) {
        toast.success('AMC details added');
        setAmcStart('');
        setAmcEnd('');
        setAmcRemark('');
        fetchAMCList();
      } else {
        toast.error(res.data.message || 'Failed to save AMC');
      }
    } catch (err) {
      toast.error('Server error while saving AMC');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card shadow p-4 mt-4">
      <h5 className="mb-3">🛠 AMC Details</h5>

      <div className="row mb-2">
        <div className="col-md-4"><strong>Start Date:</strong></div>
        <div className="col-md-4">
          <input type="date" className="form-control" value={amcStart} onChange={(e) => setAmcStart(e.target.value)} />
        </div>
      </div>

      <div className="row mb-2">
        <div className="col-md-4"><strong>End Date:</strong></div>
        <div className="col-md-4">
          <input type="date" className="form-control" value={amcEnd} onChange={(e) => setAmcEnd(e.target.value)} />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-4"><strong>AMC Remark:</strong></div>
        <div className="col-md-4">
          <textarea className="form-control" rows="2" value={amcRemark} onChange={(e) => setAmcRemark(e.target.value)} />
        </div>
      </div>

      <div className="text-end">
        <button className="btn btn-primary" disabled={saving} onClick={handleSaveAMC}>
          💾 {saving ? 'Saving...' : 'Save AMC'}
        </button>
      </div>

      <div className="mt-5">
        <h6>📜 AMC History</h6>
        {history.length === 0 ? (
          <p className="text-muted">No AMC records found.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered table-sm">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Remark</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Created At</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item, index) => (
                  <tr key={item.id}>
                    <td>{index + 1}</td>
                    <td>{item.amc_start_date}</td>
                    <td>{item.amc_end_date}</td>
                    <td>{item.amc_remark}</td>
                    <td>
                      <span className={`badge ${item.amc_status === 1 ? 'bg-success' : 'bg-secondary'}`}>
                        {item.amc_status === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{item.created_by_name || '-'}</td>
                    <td>{item.created_at}</td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleStatusToggle(item.id, item.amc_status)}>
                        Toggle
                      </button>
                      <button className="btn btn-sm btn-outline-success" onClick={() => handleAmcExtend(item.id)}>
                        +1 Week
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AmcDetailsCard;
